import { differenceInDays, endOfDay, format, isValid, parseISO } from 'date-fns'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import { ja } from 'date-fns/locale'

export const NO_EXPIRATION_DATE_JST = new Date('9999-12-31T23:59:59.999+09:00')

export function formatDate(date: Date | string, formatString = 'yyyy/MM/dd HH:mm:ss') {
  const isoString = typeof date === 'string' ? date : date.toISOString()
  return formatInTimeZone(isoString, 'Asia/Tokyo', formatString, { locale: ja })
}

// 月の最終日を取得する
// param: yearMonth: 'yyyy-MM'
// return: 月の最終日: number
export function getLastDayOfMonth(yearMonth: string) {
  const [year, month] = yearMonth.split('-').map(Number)
  const date = new Date(year, month, 0) // 月を1つ進めて0日目を取得
  return date.getDate()
}

// 日本時間の月の最終日を取得する
// return: 月の最終日: 'yyyy-MM-dd'
export function getEndOfMonthInJST(date: Date | string) {
  const isoDate = typeof date === 'string' ? parseISO(date) : date

  if (!isValid(isoDate)) {
    return `Invalid date string: ${date}`
  }

  // 入力日付をJSTに変換
  const jstDate = toZonedTime(isoDate, 'Asia/Tokyo')

  // JSTの日付で月末を計算
  const endOfMonthDate = new Date(jstDate.getFullYear(), jstDate.getMonth() + 1, 0)

  return format(endOfMonthDate, 'yyyy-MM-dd')
}

/**
 * 日付範囲の有効性をチェックする
 */
export function isDateRangeValid(startDate: Date, endDate: Date, maxDifferenceDays?: number) {
  if (startDate > endDate) {
    return false
  }
  if (maxDifferenceDays && Math.abs(differenceInDays(endDate, startDate)) > maxDifferenceDays) {
    return false
  }
  return true
}

/**
 * NOTE: toZonedTime/formatInTimeZoneの処理だと、ブラウザのタイムゾーンがJST以外の場合に時間がずれてしまうため、強制的にJSTに変換する
 * 例：タイムゾーンがSan Franciscoの場合
 * - startAt.toISOString()                                                                     -> 2025-05-20T07:00:00.000Z
 * - toZonedTime(startAt, 'Asia/Tokyo').toISOString()                                          -> 2025-05-20T23:00:00.000Z
 * - new Date(formatInTimeZone(startAt, 'Asia/Tokyo', 'yyyy-MM-dd HH:mm:ssXXX')).toISOString() -> 2025-05-20T07:00:00.000Z
 * - new Date(`${year}-${month}-${day}T00:00:00.000+09:00`)                                    -> 2025-05-19T15:00:00.000Z （この関数の結果））
 */
export function convertStartAtJST(startAt: Date) {
  const year = startAt.getFullYear()
  const month = (startAt.getMonth() + 1).toString().padStart(2, '0')
  const day = startAt.getDate().toString().padStart(2, '0')
  return new Date(`${year}-${month}-${day}T00:00:00.000+09:00`)
}

/**
 * NOTE: toZonedTimeの処理だけだと、ブラウザのタイムゾーンがJST以外の場合に時間がずれてしまうため、強制的にJSTに変換する
 * 例：タイムゾーンがSan Franciscoの場合
 * - endAt.toISOString()                            -> 9999-12-31T14:59:59.999Z
 * - toZonedTime(endAt, 'Asia/Tokyo').toISOString() -> +010000-01-01T07:59:59.999Z
 */
export function convertEndAtJST(endAt: Date) {
  // 「期限なし」の場合は、特別に「9999-12-31 T14:59:59.999（JSTの23:59:59.999）」を返す
  const isNoExpiration = endAt.getFullYear() === 9999 && endAt.getMonth() === 11 && endAt.getDate() === 31 // 月は0から始まるので12月は11
  if (isNoExpiration) {
    return new Date('9999-12-31T23:59:59.999+09:00')
  }

  // 通常の日付：同日のJSTの23:59:59.999に設定
  const jstDate = toZonedTime(endAt, 'Asia/Tokyo')
  const endOfDayDate = endOfDay(jstDate)
  const year = endOfDayDate.getFullYear()
  const month = (endOfDayDate.getMonth() + 1).toString().padStart(2, '0')
  const day = endOfDayDate.getDate().toString().padStart(2, '0')
  return new Date(`${year}-${month}-${day}T23:59:59.999+09:00`)
}

/**
 * Date オブジェクトを datetime-local 形式の文字列に変換する
 */
export function convertDateToDatetimeLocal(date: Date) {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')

  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}
